import type {
  ChangelogChannel,
  ChangelogDelivery,
  ChangelogSectionKey,
  ChangelogStatus,
} from "@/features/changelog/api/changelog.api";

/** Base for the Jira links rendered next to a bullet. */
export const JIRA_BROWSE_BASE = "https://adgeai.atlassian.net/browse";

/** Section order is fixed: what is new, then better, then repaired. */
export const CHANGELOG_SECTION_ORDER: ChangelogSectionKey[] = ["new", "improved", "fixed"];

/**
 * Channels by unit. A deploy goes to Slack and can also open the in-app
 * banner; email stays on roundups so frequent releases do not mail everyone.
 */
export const ENTRY_CHANNELS: ChangelogChannel[] = ["IN_APP", "SLACK"];
export const DIGEST_CHANNELS: ChangelogChannel[] = ["IN_APP", "EMAIL", "SLACK"];

/** Reading order for delivery rows, covering whichever channels a unit used. */
export const CHANGELOG_CHANNEL_ORDER: ChangelogChannel[] = ["IN_APP", "SLACK", "EMAIL"];

/**
 * What the editorial predicates below need. Deploy entries and digests differ
 * in where their words came from and who reads them, but they move through the
 * same generate → edit → approve → send machine, so the rules governing that
 * machine are written once against the shape they share.
 */
export interface ChangelogSendable {
  status: ChangelogStatus;
  approvedAt: string | null;
  itemCount: number;
  isGenerationStuck: boolean;
  deliveries: ChangelogDelivery[];
}

export function formatDeployedAt(value: string, locale?: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Split for table cells, which stack the clock over the date to stay narrow. */
export function getTimestampDisplay(
  value: string,
  locale?: string,
): { time: string; date: string } {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { time: "—", date: "" };
  return {
    time: parsed.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    date: parsed.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };
}

/** Populated sections only, so a row says "2 new · 1 fixed" and stays quiet otherwise. */
export function getSectionCounts(
  sections: Record<ChangelogSectionKey, unknown[]>,
): Array<{ key: ChangelogSectionKey; count: number }> {
  return CHANGELOG_SECTION_ORDER.filter((key) => sections[key].length > 0).map((key) => ({
    key,
    count: sections[key].length,
  }));
}

export function buildTicketUrl(ticket: string): string {
  return `${JIRA_BROWSE_BASE}/${encodeURIComponent(ticket)}`;
}

/**
 * The three editorial steps the card shows as a progress indicator. Generation
 * states sit outside it: something still being written has not entered the
 * editorial flow at all.
 */
export type ChangelogStage = "draft" | "approved" | "sent";

/**
 * What the status indicator says. Generation state outranks editorial state — a
 * regenerating draft reads as busy whatever happened before.
 */
export type ChangelogEntryState =
  | "generating"
  | "stalled"
  | "failed"
  | "pending"
  | "draft"
  | "approved"
  | "sending"
  | "sent";

export function getEntryState(entry: ChangelogSendable): ChangelogEntryState {
  if (entry.status === "GENERATING") {
    return entry.isGenerationStuck ? "stalled" : "generating";
  }
  if (entry.status === "FAILED") return "failed";
  if (entry.status === "PENDING") return "pending";
  if (isSending(entry)) return "sending";
  if (hasSentChannel(entry)) return "sent";
  return entry.approvedAt ? "approved" : "draft";
}

/** Which of the three editorial steps has been reached. */
export function getStage(entry: ChangelogSendable): ChangelogStage {
  if (hasSentChannel(entry) || isSending(entry)) return "sent";
  return entry.approvedAt ? "approved" : "draft";
}

/**
 * A stuck channel is excluded: its worker is gone, so calling it in flight
 * would show a spinner that never resolves and hide the force retry that is
 * the only way out.
 */
export function isSending(entry: ChangelogSendable): boolean {
  return entry.deliveries.some(
    (d) => d.status === "PENDING" || (d.status === "SENDING" && !d.isStuck),
  );
}

export function hasSentChannel(entry: ChangelogSendable): boolean {
  return entry.deliveries.some((d) => d.status === "SENT");
}

export function findDelivery(
  entry: ChangelogSendable,
  channel: ChangelogChannel,
): ChangelogDelivery | undefined {
  return entry.deliveries.find((d) => d.channel === channel);
}

/** True once a channel has gone out, which is what makes it unrepeatable. */
export function isChannelSent(entry: ChangelogSendable, channel: ChangelogChannel): boolean {
  return findDelivery(entry, channel)?.status === "SENT";
}

/** A failed channel is the one case where sending the same channel twice is right. */
export function isChannelRetryable(
  entry: ChangelogSendable,
  channel: ChangelogChannel,
): boolean {
  return findDelivery(entry, channel)?.status === "FAILED";
}

/**
 * Claimed by a worker that never came back. Retrying means overriding the
 * claim, so it is offered separately from an ordinary retry — for email it can
 * mean mailing someone twice, and the admin is the one who decides that.
 */
export function isChannelStuck(entry: ChangelogSendable, channel: ChangelogChannel): boolean {
  return findDelivery(entry, channel)?.isStuck === true;
}

/** Queued or actively sending, and therefore not something to queue again. */
export function isChannelInFlight(
  entry: ChangelogSendable,
  channel: ChangelogChannel,
): boolean {
  const delivery = findDelivery(entry, channel);
  if (!delivery) return false;
  if (delivery.status === "PENDING") return true;
  return delivery.status === "SENDING" && !delivery.isStuck;
}

/** Editing is open only on finished notes that nobody has approved yet. */
export function canEdit(entry: ChangelogSendable): boolean {
  return entry.status === "READY" && entry.approvedAt === null;
}

/** Approving needs finished notes that actually say something. */
export function canApprove(entry: ChangelogSendable): boolean {
  return entry.status === "READY" && entry.approvedAt === null && entry.itemCount > 0;
}

/**
 * Approval can be taken back until a channel is queued, sending, or sent. A
 * channel that only ever failed does not count: nothing reached anyone, and
 * that is exactly when an admin needs the notes reopened, because a failed
 * send is often a sign the content or the target was wrong.
 */
export function canRevokeApproval(entry: ChangelogSendable): boolean {
  return (
    entry.approvedAt !== null && entry.deliveries.every((d) => d.status === "FAILED")
  );
}

/** Sending needs an approval and at least one channel that has not gone out. */
export function canSend(entry: ChangelogSendable, channels: ChangelogChannel[]): boolean {
  if (entry.approvedAt === null || entry.itemCount === 0) return false;
  return channels.some((channel) => !isChannelSent(entry, channel));
}

/**
 * A row can be ticked for a roundup, a bulk post, or both. Compose still only
 * consumes composable versions; sendable-only ticks exist so an approved deploy
 * already in a roundup can still go to Slack.
 */
export function isDeployPickable(
  entry: ChangelogSendable & { versionTag: string },
  options: {
    canCompose: boolean;
    canSendEntries: boolean;
    composableVersions: ReadonlySet<string>;
  },
): boolean {
  if (options.canCompose && options.composableVersions.has(entry.versionTag)) return true;
  return options.canSendEntries && canSend(entry, ENTRY_CHANNELS);
}

/**
 * Approved notes are frozen: a send may already be in flight against them, so
 * rewriting what it was based on would leave the record disagreeing with what
 * went out. The API rejects it too — this only keeps the button from offering it.
 *
 * A `GENERATING` unit stays regenerable because force-regenerate is the
 * documented way out of a worker that died mid-run. Hiding the button here
 * would leave a stuck draft needing a database edit.
 */
export function canRegenerate(entry: ChangelogSendable): boolean {
  return entry.approvedAt === null;
}

/** An unapproved roundup can be thrown away, which frees its deploys again. */
export function canDiscard(digest: ChangelogSendable): boolean {
  return digest.approvedAt === null;
}
